<template>
	<div class="mv-s" v-if="hasPrice">
		<div class="grid grid--1-2 grid--ms-full grid--gutters-s" v-if="isRushOrderAvailable">
			<div class="cell">
				<div
					class="configurator-choice cursor-pointer"
					:class="{ 'configurator-choice--selected': !getOrderLine.isRush }"
					@click="setRush(false)">
					<span class="configurator__input-selectable" />
					<icons clock />
					<p class="configurator-choice__copy">
						<strong>Standaard productie</strong><br>
						Verwachte leverdatum {{ standardDelivery.date }}
					</p>
				</div>
			</div>
			<div class="cell">
				<div
					class="configurator-choice cursor-pointer"
					:class="{ 'configurator-choice--selected': getOrderLine.isRush }"
					@click="setRush(true)">
					<span class="configurator__input-selectable" />
					<icons dashboard />
					<p class="configurator-choice__copy">
						<strong>Spoed productie</strong><br>
						Verwachte leverdatum {{ rushDelivery.date }}
					</p>
				</div>
			</div>
		</div>
		<div class="configurator-choice configurator-choice--selected" v-else-if="standardDelivery.date">
			<icons clock />
			<p class="configurator-choice__copy">
				<strong>Standaard productie</strong><br>
				Verwachte leverdatum {{ standardDelivery.date }}
			</p>
		</div>
		<div class="production-choice mv-s" v-if="supportsOffset">
			<div class="description mv-sm">
				<p class="description__text">
					Bij het drukken kunnen wij gebruik maken van twee verschillende druktechnieken: offset en digitaal.
					Bij een kleine oplage raden we u aan om voor digitaal te kiezen.
				</p>
			</div>
			<div class="grid grid--1-2 grid--ms-full grid--gutters-s">
				<div class="cell" v-if="hasDigitalAvailable">
					<div
						class="configurator-choice cursor-pointer"
						:class="{ 'configurator-choice--selected': !getOrderLine.isOffset && offsetSelected }"
						@click="setOffset(false)">
						<span class="configurator__input-selectable" />
						<icons digitalprint />
						<p class="configurator-choice__copy">
							<strong>Digitaal</strong><br>
							Ideaal voor kleine oplages
						</p>
					</div>
				</div>
				<div class="cell">
					<div
						class="configurator-choice cursor-pointer"
						:class="{ 'configurator-choice--selected': getOrderLine.isOffset && offsetSelected }"
						@click="setOffset(true)">
						<span class="configurator__input-selectable" />
						<icons offset />
						<p class="configurator-choice__copy">
							<strong>Offset</strong><br>
							Geschikt voor grote oplages
						</p>
					</div>
				</div>
			</div>
		</div>

		<div
			class="production-choice--offset"
			v-if="offsetSelected && supportsOffset && getOrderLine.isOffset">

			<div class="price-overview-grid mv-m">
				<div class="grid grid--1-3 price-overview-table__head">
					<div class="cell">
						<div class="h4 price-overview__title ml-xl">Aantal</div>
					</div>
					<div class="cell">
						<div class="h4 price-overview__title text--center">Prijs per stuk</div>
					</div>
					<div class="cell">
						<div class="h4 price-overview__title text--right">Totaalprijs</div>
					</div>
				</div>
				<div
					class="grid grid--1-3 price-overview-row" v-for="priceRow in offsetPriceTable"
					:key="priceRow.Amount + '-offset'"
					:class="{ 'price-overview-row--active': getOrderLine.amount === priceRow.Amount }"
					@click="setOrderLineAmount(priceRow.Amount)">
					<div class="cell price-overview-row__field price-overview-row__field--amount">
						<label class="price-overview-row__input" />
						{{ priceRow.Amount }}
					</div>
					<div class="cell price-overview-row__field">
						<template v-if="!loadingPrice">
							<p class="mh-auto">
								{{ formatOffsetSinglePrice(priceRow.Single) }}
								<span class="price-overview__exclbtw">(excl.&nbsp;btw)</span>
							</p>
						</template>
						<template v-else>
							<div class="loading loading--table">
								<div />
								<div />
								<div />
							</div>
						</template>
					</div>
					<div class="cell price-overview-row__field">
						<template v-if="!loadingPrice">
							<p class="ml-auto text--right">
								{{ formatPrice(priceRow.Price) }}
								<span class="price-overview__exclbtw">(excl.&nbsp;btw)</span>
							</p>
						</template>
						<template v-else>
							<div class="loading loading--table">
								<div />
								<div />
								<div />
							</div>
						</template>
					</div>
				</div>
			</div>
			<div class="description p-s">
				<p class="description__text">
					Verwachte leverdatum {{ offsetDelivery.date }}
				</p>
			</div>
		</div>

		<div
			class="production-choice--digital"
			v-if="!supportsOffset || (offsetSelected && !getOrderLine.isOffset)">

			<div class="price-overview-grid mv-m">
				<div class="grid grid--1-3 price-overview-table__head" v-if="hasDigitalAvailable">
					<div class="cell">
						<div class="h4 price-overview__title ml-xl">Aantal</div>
					</div>
					<div class="cell">
						<div class="h4 price-overview__title text--center">Prijs per stuk</div>
					</div>
					<div class="cell">
						<div class="h4 price-overview__title text--right">Totaalprijs</div>
					</div>
				</div>
				<div
					class="grid grid--1-3 price-overview-row" v-for="priceRow in priceTable"
					v-if="priceRow.amount !== 0" :key="priceRow.amount + '-digital'"
					:class="{ 'price-overview-row--active': getOrderLine.amount === priceRow.amount }"
					@click="setOrderLineAmount(priceRow.amount)">
					<div class="cell price-overview-row__field price-overview-row__field--amount">
						<label class="price-overview-row__input" />
						{{ priceRow.amount }}
					</div>
					<div class="cell price-overview-row__field">
						<p class="mh-auto" v-if="isRushOrderAvailable && getOrderLine.isRush">
							{{ formatPrice(inclTaxOnly ? priceRow.rush.singlePriceWithTax : priceRow.rush.singlePrice) }}
							<span class="price-overview__exclbtw hide-s"> ({{ inclTaxOnly ? "incl" : "excl" }}.&nbsp;btw)</span>
						</p>
						<p class="mh-auto" v-else-if="priceRow.default.singlePrice">
							{{ formatPrice(inclTaxOnly ? priceRow.default.singlePriceWithTax : priceRow.default.singlePrice) }}
							<span class="price-overview__exclbtw hide-s"> ({{ inclTaxOnly ? "incl" : "excl" }}.&nbsp;btw)</span>
						</p>
						<div class="loading loading--table" v-else>
							<div />
							<div />
							<div />
						</div>
					</div>
					<div class="cell price-overview-row__field">
						<p class="ml-auto text--right" v-if="isRushOrderAvailable && getOrderLine.isRush">
							{{ formatPrice(inclTaxOnly ? priceRow.rush.PriceWithTax : priceRow.rush.PriceWithoutTax) }}
							<span class="price-overview__exclbtw"> ({{ inclTaxOnly ? "incl" : "excl" }}.&nbsp;btw)</span>
						</p>
						<p class="ml-auto text--right" v-else-if="priceRow.default.PriceWithoutTax">
							{{ formatPrice(inclTaxOnly ? priceRow.default.PriceWithTax : priceRow.default.PriceWithoutTax) }}
							<span class="price-overview__exclbtw"> ({{ inclTaxOnly ? "incl" : "excl" }}.&nbsp;btw)</span>
						</p>
						<div class="loading loading--table" v-else>
							<div />
							<div />
							<div />
						</div>
					</div>
				</div>
				<div
					class="grid grid--1-3 price-overview-row background-indigo-050" v-if="selectedProduct.isCustomAmountAllowed"
					:class="{ 'price-overview-row--active': !this.priceExistsInTable }"
					@click="setOrderLineAmount(getOrderLine.amount)">
					<div class="cell price-overview-row__field">
						<div class="price-overview-row__amount-container pl-xs">
							<amount table />
						</div>
					</div>
					<div class="cell price-overview-row__field">
						<template v-if="!loadingPrice">
							<p class="mh-auto text--bold" v-if="isRushOrderAvailable && getOrderLine.isRush">
								{{ formatPrice(priceSingle({ rush: true })) }}
								<span class="price-overview__exclbtw hide-s"> ({{ inclTaxOnly ? "incl" : "excl" }}.&nbsp;btw)</span>
							</p>
							<p class="mh-auto text--bold" v-else>
								{{ formatPrice(priceSingle({ rush: false })) }}
								<span class="price-overview__exclbtw hide-s"> ({{ inclTaxOnly ? "incl" : "excl" }}.&nbsp;btw)</span>
							</p>
						</template>
						<template v-else>
							<div class="loading loading--table">
								<div />
								<div />
								<div />
							</div>
						</template>
					</div>
					<div class="cell price-overview-row__field">
						<template v-if="!loadingPrice">
							<p class="ml-auto mr-xs text--right text--bold" v-if="isRushOrderAvailable && getOrderLine.isRush">
								{{ formatPrice(price({ rush: true })) }}
								<span class="price-overview__exclbtw"> ({{ inclTaxOnly ? "incl" : "excl" }}.&nbsp;btw)</span>
							</p>
							<p class="ml-auto mr-xs text--right text--bold" v-else>
								{{ formatPrice(price({ rush: false })) }}
								<span class="price-overview__exclbtw"> ({{ inclTaxOnly ? "incl" : "excl" }}.&nbsp;btw)</span>
							</p>
						</template>
						<template v-else>
							<div class="loading loading--table">
								<div />
								<div />
								<div />
							</div>
						</template>
					</div>
				</div>
			</div>

			<div class="description mv-sm" v-if="hasQuotation">
				<p class="description__text">
					Voor aantallen boven de {{ selectedFormat.maxAmount }} kunt u een offerte op maat aanvragen.
				</p>
				<button class="description__button" @click="openQuotationModal">
					Offerte aanvragen
				</button>
			</div>

		</div>
	</div>
</template>

<script>
import amount from "@/components/element/amount";
import icons from "@/components/element/icons";
import { Helper } from "@/helpers";
import { EventBus } from "@/store/index.js";

export default {
	name: "PriceTable",
	data () {
		return {
			title: "Kies een oplage",
			offsetSelected: false
		};
	},
	props: {
		overview: {
			type: Boolean,
			default: false
		}
	},
	components: {
		amount,
		icons
	},
	computed: {
		configuratorData () {
			return this.$store.getters.getConfiguratorData;
		},
		selectedProduct () {
			return this.$store.getters.getSelectedProduct;
		},
		selectedFormat () {
			return this.$store.getters.getSelectedFormat;
		},
		selectedOptions () {
			return this.$store.getters.getSelectedOptions;
		},
		isRushOrderAvailable () {
			return this.$store.getters.isRushOrderAvailable;
		},
		isRushOrderAvailableColspan () {
			return this.isRushOrderAvailable ? 2 : 1;
		},
		hasPrice () {
			return this.$store.getters.getPrice != null;
		},
		hasQuotation () {
			// data-has-quotation on the mount div opts the host site in to the
			// "Offerte aanvragen" feature. Used by DWN; SDW omits it.
			return this.$store.state.baseProps.hasQuotation === "true";
		},
		inclTaxOnly () {
			// data-prices-incl-tax-only="true" swaps the price-table to display
			// PriceWithTax / singlePriceWithTax instead of the excl-tax fields.
			return this.$store.state.baseProps.pricesInclTaxOnly === "true";
		},
		hasPriceExtra () {
			return !Helper.isEmptyObject(this.$store.getters.getPriceExtra);
		},
		hasOffsetAvailable () {
			return this.$store.getters.hasOffsetAvailable;
		},
		hasDigitalAvailable () {
			return this.$store.getters.hasDigitalAvailable;
		},
		priceTable () {
			return this.$store.getters.getPriceTable.filter(priceTable => priceTable.amount > 0);
		},
		priceExistsInTable () {
			return this.priceTable.map(line => line.amount).includes(this.getOrderLine.amount);
		},
		offsetPriceTable () {
			return this.$store.getters.getOffsetPriceTable.filter(a => a.Amount > 0);
		},
		supportsOffset () {
			return this.$store.getters.currentConfigSupportsOffset;
		},
		getOrderLine () {
			return this.$store.getters.getOrderLine;
		},
		amountData () {
			return this.$store.getters.getAmountData;
		},
		priceExtra () {
			return this.$store.getters.getPriceExtra;
		},
		rushIsAvailable () {
			return this.$store.getters.isRushOrderAvailable;
		},
		getOffsetAmounts () {
			return this.$store.getters.getOffsetPriceTable.map(line => line.Amount);
		},
		loadingPrice () {
			return this.$store.getters.getLoader.price;
		},
		standardDelivery () {
			return this.$store.getters.getStandardDelivery;
		},
		rushDelivery () {
			return this.$store.getters.getRushDelivery;
		},
		offsetDelivery () {
			return this.$store.getters.getOffsetDelivery;
		}
	},
	methods: {
		formatPrice (price) {
			if (price) {
				return Helper.toCurrencyString(price);
			}
		},
		formatOffsetSinglePrice (price) {
			if (price) {
				return Helper.toCurrencyString(price, 4);
			}
		},
		price ({ rush = false }) {
			const incl = this.inclTaxOnly;
			let price;
			if (rush) {
				price = incl
					? this.$store.state.config.price.rushPriceWithTax
					: this.$store.getters.getPriceExtra.rushPrice;
			} else {
				price = incl
					? this.$store.getters.getPrice.PriceWithTax
					: this.$store.getters.getPrice.PriceWithoutTax;
			}

			return this.formatPrice(price);
		},
		priceSingle ({ rush = false }) {
			const extra = this.$store.getters.getPriceExtra;
			const incl = this.inclTaxOnly;
			const price = rush
				? (incl ? extra.singleRushPriceWithTax : extra.singleRushPrice)
				: (incl ? extra.singlePriceWithTax : extra.singlePrice);

			return this.formatPrice(price);
		},
		setOrderLineAmount (amount) {
			this.$store.commit("SET_ORDERLINE_AMOUNT", amount);
			if (this.isOffset) {
				this.$store.commit("SET_CONFIGURATION_OFFSET_PRICE", amount);
			}
		},
		validateAmount () {
			if (this.amount < this.amountData.minAmount) {
				this.valid = false;
				this.amount = this.amountData.minAmount;
			} else if (this.amount > this.amountData.maxAmount && !this.offsetAvailable) {
				this.valid = false;
				this.amount = this.amountData.maxAmount;
			} else if (this.amount > this.amountData.maxAmount && this.offsetAvailable) {
				this.valid = true;
				this.amount = this.getClosestOffsetAmount(this.amount);
				this.$store.commit("SET_OFFSET_SELECTED", true);
			} else {
				this.valid = true;
				this.$store.commit("SET_OFFSET_SELECTED", false);
			}
		},
		getClosestOffsetAmount (amount) {
			return this.getClosest(amount, this.getOffsetAmounts);
		},
		getNextOffsetAmount (amount, offsetAmounts) {
			return offsetAmounts[offsetAmounts.indexOf(amount) + 1];
		},
		getPreviousOffsetAmount (amount, offsetAmounts) {
			return offsetAmounts[offsetAmounts.indexOf(amount) - 1];
		},
		getClosest (num, arr) {
			let curr = arr[0];
			let diff = Math.abs(num - curr);
			for (let val = 0; val < arr.length; val++) {
				const newdiff = Math.abs(num - arr[val]);
				if (newdiff < diff) {
					diff = newdiff;
					curr = arr[val];
				}
			}
			return curr;
		},
		setRush (rush) {
			this.$store.commit("SET_RUSH_SELECTED", rush);
		},
		setOffset (offset) {
			this.offsetSelected = true;
			this.$store.commit("SET_OFFSET_SELECTED", offset);
			if (offset) {
				this.setOrderLineAmount(this.getClosest(this.getOrderLine.amount, this.getOffsetAmounts));
			} else {
				this.setOrderLineAmount(this.getClosest(this.getOrderLine.amount, this.amountData.amountList));
			}
		},
		openQuotationModal () {
			EventBus.$emit("openQuoteModal");
		},
		isPastTimeLimit (cutOffHour) {
			return new Date().getHours() > parseInt(cutOffHour.split(":")[0]);
		}
	},
	created () {
		this.$store.dispatch("_getPriceTable");
		if (this.hasOffsetAvailable) {
			this.$store.dispatch("_getOffsetPriceTable");
		}
	},
	mounted () {
		if (this.overview) {
			this.getOrderLine.isOffset ? this.offsetSelected = true : this.offsetSelected = false;
		}

		if (!this.hasDigitalAvailable && this.hasOffsetAvailable) {
			this.setOffset(true);
		}
	},
	watch: {
		offsetPriceTable: function () {
			this.$store.commit("SET_FIRST_OFFSET_AMOUNT_SELECTED");
		},
		selectedOptions: function (a, b) {
			this.$store.dispatch("_getOffsetPriceTable");
			this.$store.commit("SET_FIRST_OFFSET_AMOUNT_SELECTED");
		}
	}
};
</script>
<style lang="scss">
.price-overview-grid {
	.price-overview-row {
		border-bottom: 1px solid var(--cfg-primary-tint);

		&:hover,
		&--active {
			background-color: var(--cfg-primary-tint-alt);
		}
	}

	.price-overview-row__field {
		display: flex;
		align-items: center;
		padding: 16px 0 16px 16px;
		color: #1D2939;
		font-size: 15px;
		line-height: 160%;
		cursor: pointer;
	}

	.price-overview-row__input {
		position: relative;
		display: inline-block;
		width: 24px;
		min-width: 24px;
		height: 24px;
		border-radius: 12px;
		border: 2px solid var(--cfg-primary-border);
		margin-right: 32px;
	}

	.price-overview-row--active .price-overview-row__input {
		border-color: var(--cfg-primary);

		&::after {
			content: "";
			position: absolute;
			top: 3px;
			left: 7px;
			width: 6px;
			height: 12px;
			border-color: transparent var(--cfg-primary) var(--cfg-primary) transparent;
			border-style: solid;
			border-width: 2px;
			-webkit-transform: rotate(45deg);
			transform: rotate(45deg);
		}
	}

	.price-overview__title {
		color: #51525c;
		font-size: 16px;
		font-style: normal;
		font-weight: 600;
		line-height: 150%;
		letter-spacing: 0.25px;
	}

	.price-overview__exclbtw {
		color: #98A2B3;
		font-size: 11px;
		font-weight: 400;
		line-height: 160%;
	}
}

.price-overview-table {
	border-radius: 5px;
	overflow: hidden;
	background: #f5f5f5;
	border: none;

	.table__row {
		border-bottom: none;

		&--custom {
			background: #e5e5e5;

			.table__data--amount {
				background: #e5e5e5;
				vertical-align: middle;
			}
		}

		&--custom.table__row--active .table__data--amount {
			background: #e5e5e5;
		}

		&--active {
			background: #f185a4 !important;
			color: #fff;

			.table__data--amount {
				background: #d45478;
				color: #fff;
			}
		}

		&--border-top {
			border-radius: 4px 4px 0 0;
			overflow: hidden;
		}

		&--no-border-top {
			border-top: none !important;
		}

		&--no-border-bottom {
			border-bottom: none !important;
		}

		&--spacer {
			background-color: #ffffff;
			height: 30px;
			width: 100%;
		}
	}

	.table__data {
		position: relative;
		cursor: pointer;
		padding: 0;
		vertical-align: top;

		&--amount {
			cursor: initial;
			vertical-align: middle;
		}

		&--active {
			background: #e73467;
			color: #fff;

			.table__data-holder--main {
				background: #e73467;
			}
		}

		&-holder {
			display: flex;

			&--main {
				background: #f185a4;
				color: #fff;
			}
		}
	}

	.table__head {
		border-bottom: none;
	}

	.table__head-item {
		padding: 7px 15px;
		color: #fff;
		font-weight: 300;
		text-align: center;
		background-color: #438db4;

		&--border-top-left-radius {
			border-top-left-radius: 5px;
		}

		&--border-top-right-radius {
			border-top-right-radius: 5px;
		}
	}

	.table__data--disabled {
		pointer-events: none;
		cursor: disabled;
		opacity: 0.6;
		background: #eee;
		text-align: center;
	}

	.table__head-item--amount {
		background: #2a7eaa;
		vertical-align: middle;
		text-align: center;
		text-transform: uppercase;
	}

	.table__data--amount {
		background: #e5e5e5;
		text-align: center;
		color: #353535;
		font-weight: 700;
	}

	.table__data--quotation {
		background: #f3f3f3;
		text-align: center;
		color: #555;
		padding: 10px;

		strong {
			font-weight: 700;
		}
	}

	.price-overview__title {
		text-transform: uppercase;
		font-weight: 700;
		color: #fff;
		font-size: 15px;

		small {
			clear: both;
			display: block;
			padding-top: 10px;
			font-size: 12px;
			color: #fddd05;
			line-height: 12px;
			font-weight: 400;
		}
	}

	.price-overview__span {
		font-size: 13px;
	}

	.price-overview__price {
		text-align: center;
		font-weight: 700;
		flex: 1;
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 1.5rem;
		font-weight: 700;
	}

	.price-overview__piece {
		line-height: 11px;
		display: inline-block;
		font-size: 11px;
		background: rgba(0, 0, 0, 0.03);
		padding: 5px 0;
		text-align: center;
		width: 75px;
	}

	.price-overview__excl-vat {
		font-weight: 300;
		font-size: 13px;
		margin-left: 1rem;
	}

	.price-overview__piece-text {
		font-size: 11px;
		display: block;
		line-height: 11px;
	}
}

.price-overview-table tbody .table__row:not(:last-child) {
	border-bottom: 1px solid #ccc;
	background: #fff;
}

.price-overview-table .table__row:nth-child(even):not(:last-child) {
	background: #ebf1f5;
}

.configurator-choice {
	position: relative;
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 24px;
	border: 2px solid var(--cfg-primary-faint);
	border-radius: 8px;

	&:hover {
		background: var(--cfg-primary-tint);
	}

	&--selected {
		border-color: var(--cfg-primary);
		background: var(--cfg-primary-tint);
	}
}

.configurator-choice--selected {
	.configurator__input-selectable {
		border-color: var(--cfg-primary);

		&::after {
			content: "";
			position: absolute;
			top: 2px;
			left: 6px;
			width: 5px;
			height: 10px;
			border-color: transparent var(--cfg-primary) var(--cfg-primary) transparent;
			border-style: solid;
			border-width: 2px;
			-webkit-transform: rotate(45deg);
			transform: rotate(45deg);
		}
	}
}

.configurator__icon-container {
	padding: 8px;
	background: var(--cfg-primary-tint);
	border-radius: 8px;
}

@media screen and (max-width: 725px) {
	.price-overview-grid .price-overview-row__input {
		margin-right: 16px;
	}

	.price-overview-table {
		.table:not(.table--initial) .table__data:not(.table__data--amount):before {
			content: attr(data-name);
			font-size: 10px;
			background: rgba(0, 0, 0, 0.1);
			border-radius: 5px;
			padding: 12px;
			line-height: 0;
			vertical-align: 10px;
			display: inline-block;
			margin: 3px 0 0 6px;
			position: absolute;
			left: 0;
			width: 70px;
			text-align: center;
		}

		.table__row--custom-active .table__data:not(.table__data--amount):before {
			color: white;
		}

		.table__row:not(:first-child) {
			border-top: 10px solid white;
		}

		.table__row--active {
			background: transparent;
		}

		.table:not(.table--initial) .table__data.table__data--small-head {
			font-size: 18px;
			text-align: center;
		}

		.table:not(.table--initial) .table__data.table__data--ms-hidden {
			display: none;
		}

		.price-overview__price {
			font-size: 12px;
			padding-left: 75px;
		}

		.price-overview__piece {
			font-size: 11px;
		}

		.price-overview__excl-vat {
			font-weight: 300;
			font-size: 11px;
		}

		.price-overview__piece-text {
			font-size: 10px;
		}

		.price-overview-table .table .table__row,
		.table:not(.table--initial) .table__data {
			border-bottom: none;
		}

		.table__row:not(.table__row--custom) [data-name="Spoed"],
		.table__row:not(.table__row--custom) [data-name="Offset"] {
			border-top: 1px solid #ccc;
		}

		.table.table--show-head.table--auto .table__head .table__row .table__head-item,
		.table__head-item--offset {
			display: block;
			width: 100%;
		}

		.table__head-item .price-overview__title {
			font-size: 15px;
		}

		.table.table--show-head.table--auto .table__head .table__row .table__head-item--amount {
			display: none;
		}

		.table--standard-only.table:not(.table--initial) .table__data:not(.table__data--amount):before {
			display: none;
		}

		.table__data.table__data--quotation {
			display: none !important;
		}
	}
}
</style>



// WEBPACK FOOTER //
// src/components/element/price-table.vue